import { Link } from "@inertiajs/react";
import { colors } from "./constants";
import { faChevronLeft, faChevronRight, faMagnifyingGlass, faMinus, faPlus, faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { BibleNavigation, TranslationSelect } from "./BibleNavigation";
import { useScreenSize } from "../hooks/useScreenSize";
import { useState } from "react";
import { navigateBible, useCurrentSearch } from "./routing";
import { RestoreChromeButtons } from "./ChromeVisibility";
import AppAppearance from "./AppAppearance";

export function IconButton({ onClick, icon, children, active = false, style = {} }: { onClick?: () => void, icon?: any, children?: React.ReactNode, active?: boolean, style?: {} }) {
  const { screenLargerThan } = useScreenSize()
  const compact = !screenLargerThan('lg')
  return (
    <button
      style={{
        cursor: 'pointer',
        fontSize: compact ? '13px' : '15px',

        border: '1px solid',
        // backgroundColor: active ? colors['darkPrimary'] : colors['light'],
        // borderColor: active ? colors['light'] : colors['borderColor'],
        // color: active ? colors['light'] : colors['darkPrimary'],

        backgroundColor: active ? 'var(--active-button-background)' : colors['buttonBackground'],
        borderColor: active ? 'var(--active-button-border)' : colors['buttonBorder'],
        color: active ? 'var(--active-button-text)' : colors['buttonText'],

        aspectRatio: '1 / 1',
        minWidth: compact ? '32px' : '40px',
        flex: '0 0 auto',
        height: '100%',
        // width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...style
      }}
      onClick={onClick}
    >
      {icon && (
        <FontAwesomeIcon
          icon={icon}
          style={{
            marginRight: children ? '10px' : '0px'
          }}
        />
      )}
      {children && (
        children
      )}
    </button>
  )
}

export function NavigationButtonLink({ href, direction }: { href: string, direction: 'left' | 'right' }) {
  const currentSearch = useCurrentSearch();
  const hrefWithSearch = href + currentSearch;
  return (
    <Link
      href={hrefWithSearch}
      style={{ textDecoration: 'none' }}
      preserveState={true}
      only={[
        'chosen_book',
        'chosen_chapter',
        'verses',
      ]}
    >
      <IconButton icon={direction == 'left' ? faChevronLeft : faChevronRight} />
    </Link>
  )
}

export function NumberInput({
  icon,
  value,
  max = 99,
  onChange,
}: {
  icon?: any;
  value?: number;
  max?: number;
  onChange: (newValue: number) => void;
}) {
  const { screenLargerThan } = useScreenSize()
  const compact = !screenLargerThan('lg')
  // The +/- buttons size themselves from IconButton's min-width; pin the whole
  // control to that same height so the value box lines up with them exactly.
  const controlHeight = compact ? 32 : 40
  return (
    <div style={{
      flexDirection: 'row',
      height: `${controlHeight}px`,
      display: 'flex',
      gap: '4px',
    }}>
      <IconButton
        icon={faMinus}
        onClick={() => {
          onChange(Math.max(1, (value || 1) - 1));
        }}
      />
      <div
        style={{
          height: '100%',
          border: `1px solid ${colors['borderColor']}`,
          color: colors['darkPrimary'],
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          // Was 8px with a 25px value below, which made this box noticeably
          // taller and wider than the +/- buttons next to it.
          padding: '0px 6px',
          boxSizing: 'border-box',
        }}
      >
        {icon && (
          <div style={{
            marginRight: '6px',
            borderRight: `1px solid ${colors['borderColor']}`,
            paddingRight: '6px',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: colors['black'],

            fontSize: compact ? '11px' : '13px',
          }}>
            <FontAwesomeIcon icon={icon} />
          </div>
        )}
        <div style={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          // Matches the +/- buttons' own font size so the value box no longer
          // towers over them.
          fontSize: compact ? '13px' : '15px',
          color: colors['black'],
          fontFamily: 'Times New Roman, serif',
        }}>
          {value}
        </div>
      </div>
      <IconButton
        icon={faPlus}
        onClick={() => {
          onChange(Math.min(max, (value || 1) + 1));
        }}
      />
    </div>
  )
}

interface BibleNavigationDialogProps {
  currentTranslation: any;
  currentBook: any;
  currentChapter: any;
  books: any[];
  translations: any[];
}

function BibleNavigationDialog({
  currentTranslation,
  currentBook,
  currentChapter,
  books,
  translations
}: BibleNavigationDialogProps) {
  const { screenLargerThan } = useScreenSize()
  const compact = !screenLargerThan('lg')
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'center',
      gap: '4px',
      minWidth: 0,
      // Sized to its content, but allowed to shrink on mobile so a long book name
      // ellipsizes instead of pushing the search button off the row.
      flexShrink: compact ? 1 : 0,
    }}>
      <NavigationButtonLink
        href={currentChapter.previous_chapter_path}
        direction="left"
      />
      <BibleNavigation
        currentTranslation={currentTranslation}
        currentBook={currentBook}
        currentChapter={currentChapter}
        books={books}
        translations={translations}
        key={`bible-navigation-${currentTranslation.slug}-${currentBook.slug}-${currentChapter.chapter_number}`}
      />
      <NavigationButtonLink
        href={currentChapter.next_chapter_path}
        direction="right"
      />
    </div>
  )
}

function DesktopBibleTopBar({
  currentTranslation,
  currentBook,
  currentChapter,
  books,
  translations,
}: any) {
  return (
    <div style={{
      width: '100%',
      display: 'flex',
      height: '50px',
      padding: '4px',
      flexDirection: 'row',
      borderBottom: `1px solid ${colors['borderColor']}`,
      justifyContent: 'space-between',
      alignItems: 'center',
      backgroundColor: colors['background'],
    }}>
      <BibleNavigationDialog
        currentTranslation={currentTranslation}
        currentBook={currentBook}
        currentChapter={currentChapter}
        books={books}
        translations={translations}
      />
      <div style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: '8px',
      }}>
        <RestoreChromeButtons size={40} />
        <AppAppearance />
      </div>
    </div>
  )
}

function MobileBibleTopBar({
  currentTranslation,
  currentBook,
  currentChapter,
  books,
  translations,
  queriedBooks,
  queriedVerses,
  chosenBook,
  queryInput,
  setQueryInput,
  searchInputRef,
}: any) {
  const [searchOpen, setSearchOpen] = useState(false);

  const currentLocation = {
    translationSlug: currentTranslation.slug,
    bookSlug: currentBook.slug,
    chapterNumber: currentChapter.chapter_number,
  };

  const navigateToFirstResult = () => {
    if (queriedVerses && queriedVerses.length > 0) {
      const firstVerse = queriedVerses[0];
      navigateBible(currentLocation, {
        translationSlug: firstVerse[0].bible_translation_slug,
        bookSlug: firstVerse[0].bible_book_slug,
        chapterNumber: firstVerse[0].chapter_number,
        verseNumber: firstVerse[0].verse_number,
        clearQuery: true,
      });
      setSearchOpen(false);
    }
    else if (queriedBooks && queriedBooks.length > 0) {
      navigateBible(currentLocation, {
        bookSlug: queriedBooks[0].slug,
        chapterNumber: 1,
        clearQuery: true,
      });
      setSearchOpen(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      navigateToFirstResult();
    }
  };

  const handleClearSearch = () => {
    navigateBible(currentLocation, { query: '' });
    setQueryInput('');
    setSearchOpen(false);
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: colors['background'],
    }}>
      <div style={{
        width: '100%',
        display: 'flex',
        height: '40px',
        padding: '4px',
        flexDirection: 'row',
        borderBottom: `1px solid ${colors['borderColor']}`,
        justifyContent: 'center',
        position: 'relative',
        gap: '4px',
        minWidth: 0,
      }}>
        {searchOpen || queryInput ? (
          <>
            <TranslationSelect
              currentTranslation={currentTranslation}
              currentBook={currentBook}
              currentChapter={currentChapter}
              translations={translations}
            />
            <input
              ref={searchInputRef}
              type="text"
              enterKeyHint="go"
              placeholder="Search: Son of Man, John 3:16, Matthew..."
              className="custom-input"
              value={queryInput}
              autoFocus
              style={{
                height: '100%',
                borderTop: `1px solid ${colors['borderColor']}`,
                borderBottom: `1px solid ${colors['borderColor']}`,
                borderLeft: 'none',
                borderRight: 'none',
                padding: '8px',
                fontSize: '16px',
                boxSizing: 'border-box',
                flex: 1,
                outline: 'none',
                backgroundColor: 'transparent',
                color: colors['black'],
              }}
              onChange={(e) => {
                navigateBible(currentLocation, { query: e.target.value });
                setQueryInput(e.target.value);
              }}
              onKeyDown={handleKeyPress}
            />
            <div style={{
              flex: 0,
              display: 'flex',
              flexDirection: 'row',
              gap: '4px',
            }}>
              <IconButton
                icon={faXmark}
                onClick={handleClearSearch}
              />
            </div>
          </>
        ) : (
          <>
            <BibleNavigationDialog
              currentTranslation={currentTranslation}
              currentBook={currentBook}
              currentChapter={currentChapter}
              books={books}
              translations={translations}
            />
            <IconButton
              style={{
                marginLeft: 'auto',
              }}
              onClick={() => {
                setSearchOpen(true);
              }}
            >
              <FontAwesomeIcon icon={faMagnifyingGlass} />
            </IconButton>
          </>
        )}
      </div>
      {/* {searchOpen && query && (
        <BibleSearchDialog
          queriedBooks={queriedBooks}
          queriedVerses={queriedVerses}
          chosenBook={chosenBook}
          onClose={() => setSearchOpen(false)}
        />
      )} */}
    </div>
  )
}

interface BibleTopBarProps {
  currentTranslation: any;
  currentBook: any;
  currentChapter: any;
  books: any[];
  translations: any[];
  queriedBooks?: any[];
  queriedVerses?: any[];
  chosenBook?: any;
  searchInputRef?: any;
  queryInput: string;
  setQueryInput: (value: string) => void;
}

export default function BibleTopBar({
  currentTranslation,
  currentBook,
  currentChapter,
  books,
  translations,
  queriedBooks,
  queriedVerses,
  chosenBook,
  searchInputRef,
  queryInput,
  setQueryInput,
}: BibleTopBarProps) {
  const { screenLargerThan } = useScreenSize();

  return (
    screenLargerThan('lg') ? (
      <DesktopBibleTopBar
        currentTranslation={currentTranslation}
        currentBook={currentBook}
        currentChapter={currentChapter}
        books={books}
        translations={translations}
      />
    ) : (
      <MobileBibleTopBar
        currentTranslation={currentTranslation}
        currentBook={currentBook}
        currentChapter={currentChapter}
        books={books}
        translations={translations}
        queriedBooks={queriedBooks}
        queriedVerses={queriedVerses}
        chosenBook={chosenBook}
        queryInput={queryInput}
        setQueryInput={setQueryInput}
        searchInputRef={searchInputRef}
      />
    )
  )
}
